import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Search, ChevronRight, Plus, EyeOff, Eye } from "lucide-react";
import CreateStatementModal from "@/components/CreateStatementModal";
import {
    BulkActionsBar, BulkConfirmModal, BulkResultModal,
} from "@/components/BulkStatementActions";
import {
    formatStatementIdentifier, formatStatementPeriod,
} from "@/lib/statementDisplay";

const STATUS_FILTERS = [
    { value: "", label: "All" },
    { value: "draft", label: "Draft" },
    { value: "sent", label: "Sent" },
    { value: "processing", label: "Processing" },
    { value: "paid", label: "Paid" },
    { value: "failed", label: "Failed" },
    { value: "void", label: "Void" },
];

const STATUS_COLOR = {
    draft: "bg-slate-100 text-slate-700",
    sent: "bg-blue-50 text-blue-900",
    processing: "bg-amber-50 text-amber-900",
    paid: "bg-emerald-100 text-emerald-900",
    failed: "bg-red-50 text-red-700",
    void: "bg-slate-200 text-slate-700",
    // Phase K.4 (2026-08-07) — refund lifecycle chips.
    partially_refunded: "bg-amber-50 text-amber-900",
    refunded: "bg-slate-200 text-slate-700",
};

const HIDE_VOIDED_KEY = "statements_hide_voided";

function fmtMoney(cents) {
    if (cents == null) return "—";
    const v = Math.abs(cents) / 100;
    return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function AdminStatementsListPage() {
    const [params, setParams] = useSearchParams();
    const status = params.get("status") || "";
    const q = params.get("q") || "";

    // Phase K.1b — hide voided default ON, persisted per-operator.
    const [hideVoided, setHideVoided] = useState(() => {
        try {
            const raw = window.localStorage.getItem(HIDE_VOIDED_KEY);
            return raw === null ? true : raw === "true";
        } catch (_) {
            return true;
        }
    });
    useEffect(() => {
        try {
            window.localStorage.setItem(HIDE_VOIDED_KEY, String(hideVoided));
        } catch (_) { /* ignore */ }
    }, [hideVoided]);

    const [rawItems, setRawItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    // Phase N.1 (2026-08-21) — bulk multi-select. Kept client-side only.
    // Clears on filter change (see useEffect below).
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [pendingAction, setPendingAction] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);

    const reload = async () => {
        setLoading(true);
        try {
            const searchParams = { limit: 100 };
            if (status) searchParams.status = status;
            const { data } = await api.get("/admin/statements", { params: searchParams });
            setRawItems(data.items || []);
            setTotal(data.total || (data.items || []).length);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
    }, [status]);

    // N.1 — clear selection whenever filters change or the list reloads.
    useEffect(() => { setSelectedIds(new Set()); }, [status, q]);

    // Row helpers.
    function toggleRow(id) {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }
    function toggleAllVisible(visibleRows) {
        setSelectedIds((prev) => {
            const allSelected = visibleRows.every((r) => prev.has(r.id));
            if (allSelected) {
                const next = new Set(prev);
                visibleRows.forEach((r) => next.delete(r.id));
                return next;
            }
            const next = new Set(prev);
            visibleRows.forEach((r) => next.add(r.id));
            return next;
        });
    }

    async function runBulkAction(payload) {
        if (!pendingAction) return;
        setBulkBusy(true);
        try {
            const { data } = await api.post(
                `/admin/statements/bulk-${pendingAction}`,
                payload,
            );
            setBulkResult(data);
            setPendingAction(null);
            setSelectedIds(new Set());
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBulkBusy(false);
        }
    }

    // Client-side filters: search query + hide-voided.
    const items = useMemo(() => {
        let list = rawItems;
        if (hideVoided && !status) {
            // Only auto-hide voided when NOT explicitly filtering to a status
            // (an operator who selects "Void" clearly wants to see them).
            list = list.filter((s) => s.status !== "void");
        }
        if (hideVoided && status === "void") {
            // If the user filtered to void AND hide-voided is on, honor
            // the explicit filter (they clicked "Void" chip).
            // list stays as-is; the API already narrows to void.
        }
        if (q) {
            const needle = q.toLowerCase();
            list = list.filter(
                (s) =>
                    (s.statement_number || "").toLowerCase().includes(needle) ||
                    (s.customer_name || "").toLowerCase().includes(needle) ||
                    (s.id || "").toLowerCase().includes(needle) ||
                    (s.customer_id || "").toLowerCase().includes(needle)
            );
        }
        return list;
    }, [rawItems, hideVoided, status, q]);

    const hiddenVoidedCount = useMemo(() => {
        if (!hideVoided || status) return 0;
        return rawItems.filter((s) => s.status === "void").length;
    }, [rawItems, hideVoided, status]);

    return (
        <DashboardShell>
            <div className="space-y-6" data-testid="admin-statements-list-page">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-3xl font-semibold flex items-center gap-2">
                            <FileText className="h-7 w-7" />
                            Consolidated Statements
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Bundle multiple invoices into ONE ACH charge per customer per period.
                        </p>
                    </div>
                    <Button
                        onClick={() => setShowCreateModal(true)}
                        data-testid="btn-create-statement-global"
                    >
                        <Plus className="h-4 w-4 mr-1" /> Create Statement
                    </Button>
                </div>

                <Card>
                    <CardContent className="pt-6">
                        <div className="flex flex-wrap gap-2 items-center">
                            {STATUS_FILTERS.map((s) => (
                                <button
                                    key={s.value || "_all"}
                                    data-testid={`statements-filter-${s.value || "all"}`}
                                    onClick={() => {
                                        const p = new URLSearchParams(params);
                                        if (s.value) p.set("status", s.value);
                                        else p.delete("status");
                                        setParams(p);
                                    }}
                                    className={`px-3 py-1.5 rounded-full text-sm border transition ${
                                        status === s.value
                                            ? "bg-slate-900 text-white border-slate-900"
                                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                                    }`}
                                >
                                    {s.label}
                                </button>
                            ))}

                            <button
                                type="button"
                                onClick={() => setHideVoided(!hideVoided)}
                                data-testid="toggle-hide-voided"
                                title={
                                    hideVoided
                                        ? "Voided statements are hidden — click to show all"
                                        : "Showing all statuses — click to hide voided"
                                }
                                className={`px-3 py-1.5 rounded-full text-sm border transition inline-flex items-center gap-1.5 ${
                                    hideVoided
                                        ? "bg-slate-50 text-slate-700 border-slate-300"
                                        : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
                                }`}
                            >
                                {hideVoided ? (
                                    <>
                                        <EyeOff className="h-3.5 w-3.5" /> Hide voided
                                    </>
                                ) : (
                                    <>
                                        <Eye className="h-3.5 w-3.5" /> Show voided
                                    </>
                                )}
                            </button>

                            <div className="relative ml-auto w-full md:w-72">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <Input
                                    data-testid="statements-search-input"
                                    placeholder="Search by number, customer, or ID…"
                                    className="pl-9"
                                    value={q}
                                    onChange={(e) => {
                                        const p = new URLSearchParams(params);
                                        if (e.target.value) p.set("q", e.target.value);
                                        else p.delete("q");
                                        setParams(p);
                                    }}
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-0">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                                <tr>
                                    <th className="w-10 pl-4 py-3">
                                        <input
                                            type="checkbox"
                                            aria-label="Select all visible"
                                            data-testid="bulk-select-all-checkbox"
                                            checked={items.length > 0 && items.every((r) => selectedIds.has(r.id))}
                                            onChange={() => toggleAllVisible(items)}
                                        />
                                    </th>
                                    <th className="text-left px-4 py-3">Statement</th>
                                    <th className="text-left px-4 py-3">Customer</th>
                                    <th className="text-left px-4 py-3">Period</th>
                                    <th className="text-right px-4 py-3">Total</th>
                                    <th className="text-left px-4 py-3">Status</th>
                                    <th className="text-left px-4 py-3">Invoices</th>
                                    <th className="w-8" />
                                </tr>
                            </thead>
                            <tbody data-testid="statements-list-body">
                                {loading && items.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="py-8 text-center text-slate-500">
                                            Loading…
                                        </td>
                                    </tr>
                                )}
                                {!loading && items.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="py-16">
                                            <div
                                                className="flex flex-col items-center justify-center gap-3 text-center"
                                                data-testid="statements-empty-state"
                                            >
                                                <div className="p-3 rounded-full bg-slate-100">
                                                    <FileText className="h-8 w-8 text-slate-400" />
                                                </div>
                                                <div>
                                                    <div className="text-slate-700 font-medium">
                                                        No statements to show
                                                    </div>
                                                    <p className="text-sm text-slate-500 mt-1 max-w-md">
                                                        {rawItems.length === 0
                                                            ? "Bundle a customer's outstanding invoices into a single ACH charge."
                                                            : "Nothing matches the current filters. Adjust filters above, or create a new statement."}
                                                    </p>
                                                </div>
                                                <Button
                                                    onClick={() => setShowCreateModal(true)}
                                                    data-testid="statements-empty-create-btn"
                                                >
                                                    <Plus className="h-4 w-4 mr-1" /> Create Statement
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                {items.map((s) => (
                                    <tr
                                        key={s.id}
                                        className="border-t hover:bg-slate-50"
                                        data-testid={`statement-row-${s.id}`}
                                    >
                                        <td className="pl-4 py-3">
                                            <input
                                                type="checkbox"
                                                aria-label={`Select statement ${s.id}`}
                                                data-testid={`bulk-row-checkbox-${s.id}`}
                                                checked={selectedIds.has(s.id)}
                                                onChange={() => toggleRow(s.id)}
                                            />
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            <Link
                                                to={`/admin/statements/${s.id}`}
                                                className="text-blue-700 hover:underline font-medium"
                                                title={s.id}
                                                data-testid={`statement-number-${s.id}`}
                                            >
                                                {formatStatementIdentifier(s)}
                                            </Link>
                                        </td>
                                        <td className="px-4 py-3">
                                            <Link
                                                to={`/admin/customers/${s.customer_id}`}
                                                className="text-slate-700 hover:underline"
                                                title={s.customer_id}
                                                data-testid={`statement-customer-${s.id}`}
                                            >
                                                {s.customer_name || `${(s.customer_id || "").slice(0, 12)}…`}
                                            </Link>
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 text-sm"
                                            data-testid={`statement-period-${s.id}`}>
                                            {formatStatementPeriod(
                                                s.period_start_date || s.period_start,
                                                s.period_end_date || s.period_end,
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right font-medium">
                                            {fmtMoney(s.total_cents)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                    STATUS_COLOR[s.status] || "bg-slate-100 text-slate-700"
                                                }`}
                                                data-testid={`statement-status-${s.id}`}
                                            >
                                                {s.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-slate-600">
                                            {(s.invoice_ids || []).length}
                                        </td>
                                        <td className="px-2">
                                            <Link to={`/admin/statements/${s.id}`}>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    data-testid={`statement-open-${s.id}`}
                                                >
                                                    <ChevronRight className="h-4 w-4" />
                                                </Button>
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {(items.length > 0 || hiddenVoidedCount > 0) && (
                            <div className="px-4 py-2 text-xs text-slate-500 bg-slate-50 border-t flex items-center justify-between">
                                <div>
                                    Showing {items.length} of {total}
                                </div>
                                {hiddenVoidedCount > 0 && (
                                    <button
                                        onClick={() => setHideVoided(false)}
                                        className="text-slate-500 hover:text-slate-800 underline"
                                        data-testid="statements-show-hidden-voided"
                                    >
                                        {hiddenVoidedCount} voided hidden — show
                                    </button>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            <CreateStatementModal
                open={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreated={() => reload()}
            />

            {/* Phase N.1 (2026-08-21) — bulk multi-select action bar +
                confirm/result modals. Rendered inside DashboardShell so
                the sticky footer clears the fixed side-nav. */}
            <BulkActionsBar
                selectedRows={items.filter((r) => selectedIds.has(r.id))}
                onAction={(action) => setPendingAction(action)}
                onClear={() => setSelectedIds(new Set())}
            />
            <BulkConfirmModal
                open={!!pendingAction}
                action={pendingAction}
                selectedRows={items.filter((r) => selectedIds.has(r.id))}
                busy={bulkBusy}
                onCancel={() => setPendingAction(null)}
                onConfirm={runBulkAction}
            />
            <BulkResultModal
                open={!!bulkResult}
                result={bulkResult}
                onClose={() => setBulkResult(null)}
            />
        </DashboardShell>
    );
}
